export default function processChunkedPusherItem({
  chunkBuffer,
  type,
  itemData,
  logger = console,
}) {
  let nextChunkBuffer = chunkBuffer;

  // Never attempt to reassemble without a stable chunk_id; otherwise we'd risk mixing messages.
  if (!itemData?.chunk_id) {
    logger?.warn?.('Received chunked item without chunk_id; skipping reassembly', {
      type,
      chunk_index: itemData?.chunk_index,
      chunk_total: itemData?.chunk_total,
    });
    return { status: 'drop', chunkBuffer: nextChunkBuffer };
  }

  const chunkId = itemData.chunk_id;
  const chunkIndex = Number(itemData.chunk_index);
  const chunkTotal = Number(itemData.chunk_total);

  if (
    !Number.isInteger(chunkIndex) ||
    !Number.isInteger(chunkTotal) ||
    chunkTotal <= 0 ||
    chunkIndex < 0 ||
    chunkIndex >= chunkTotal
  ) {
    logger?.warn?.('Invalid chunk metadata; skipping reassembly', {
      type,
      chunk_id: itemData.chunk_id,
      chunk_index: itemData.chunk_index,
      chunk_total: itemData.chunk_total,
    });
    return { status: 'drop', chunkBuffer: nextChunkBuffer };
  }

  // Initialize (or reset) buffer for this chunk ID if needed.
  // IMPORTANT: use a *dense* array so Array.every() checks missing indices.
  const existing = chunkBuffer?.[chunkId];
  const shouldReset = !existing || existing.type !== type || existing.chunkTotal !== chunkTotal;

  const nextEntry = shouldReset
    ? {
        chunks: new Array(chunkTotal).fill(undefined),
        chunkTotal,
        type,
        metadata: { ...itemData },
      }
    : {
        ...existing,
        chunks: existing.chunks.slice(),
      };

  nextEntry.chunks[chunkIndex] = itemData;

  nextChunkBuffer = {
    ...(chunkBuffer || {}),
    [chunkId]: nextEntry,
  };

  // Check if all chunks received
  const allChunks = nextChunkBuffer[chunkId].chunks;
  const allReceived = allChunks.every((chunk) => chunk !== undefined);

  if (!allReceived) {
    return { status: 'pending', chunkBuffer: nextChunkBuffer };
  }

  const firstChunk = allChunks.find((chunk) => chunk !== undefined);
  if (!firstChunk) {
    logger?.warn?.('Chunk buffer completed but no chunk data found; dropping', {
      type,
      chunk_id: chunkId,
      chunk_total: chunkTotal,
    });
    const { [chunkId]: _removed, ...rest } = nextChunkBuffer;
    nextChunkBuffer = rest;
    return { status: 'drop', chunkBuffer: nextChunkBuffer };
  }

  const reassembled = { ...nextChunkBuffer[chunkId].metadata };

  delete reassembled.chunk_index;
  delete reassembled.chunk_total;
  delete reassembled.chunk_id;

  if (type === 'text' && firstChunk.text !== undefined) {
    reassembled.text = allChunks.map((chunk) => chunk.text).join('');
  } else if (type === 'tool-response' && firstChunk.response !== undefined) {
    const responseStr = allChunks.map((chunk) => chunk.response).join('');
    const isJson = firstChunk.response_is_json;
    if (isJson) {
      try {
        reassembled.response = JSON.parse(responseStr);
      } catch (e) {
        reassembled.response = responseStr;
      }
    } else {
      reassembled.response = responseStr;
    }
  } else if (type === 'tool' && firstChunk.args !== undefined) {
    const argsStr = allChunks
      .map((chunk) =>
        typeof chunk.args === 'string'
          ? chunk.args.replace(/^\[CHUNK \d+\/\d+\] /, '')
          : JSON.stringify(chunk.args),
      )
      .join('');

    try {
      reassembled.args = JSON.parse(argsStr);
    } catch (e) {
      reassembled.args = argsStr;
    }
  }

  const { [chunkId]: _removed, ...rest } = nextChunkBuffer;
  nextChunkBuffer = rest;

  return { status: 'complete', itemData: reassembled, chunkBuffer: nextChunkBuffer };
}
